import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { Scale, Calculator, X, CheckCircle } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import { useTheme } from "context/ThemeContext";

const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const fmt = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;
const CURR_FY = "2025-26";

export default function TaxDeductions() {
  const { T } = useTheme();
  const inp = { width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" };
  const lbl = { fontSize:11.5, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" };
  const REGIME_COLOR = { Old:T.amber, New:T.cyan };

  const [declarations, setDeclarations] = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [open,         setOpen]         = useState(false);
  const [compute,      setCompute]      = useState(null);
  const [computing,    setComputing]    = useState(false);
  const [form, setForm] = useState({
    employee_id:"", financial_year:CURR_FY, regime:"New",
    house_rent_paid:0, section_80c:0, section_80d:0,
    section_80g:0, section_80e:0, other_deductions:0,
  });

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [dRes, eRes] = await Promise.all([
        axios.get(APIConstant.taxDeclarations,              ax()),
        axios.get(`${APIConstant.employees}?status=Active`, ax()),
      ]);
      setDeclarations(dRes.data || []);
      setEmployees(eRes.data   || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openDeclare = (decl=null) => {
    setCompute(null);
    if (decl) {
      setForm({ employee_id:decl.employee_id, financial_year:decl.financial_year,
                regime:decl.regime, house_rent_paid:decl.house_rent_paid,
                section_80c:decl.section_80c, section_80d:decl.section_80d,
                section_80g:decl.section_80g, section_80e:decl.section_80e,
                other_deductions:decl.other_deductions });
    } else {
      setForm({ employee_id:"", financial_year:CURR_FY, regime:"New",
                house_rent_paid:0, section_80c:0, section_80d:0,
                section_80g:0, section_80e:0, other_deductions:0 });
    }
    setOpen(true);
  };

  const handleCompute = async () => {
    if (!form.employee_id) return;
    setComputing(true);
    try {
      const r = await axios.get(`${APIConstant.taxCompute}${form.employee_id}?regime=${form.regime}`, ax());
      setCompute(r.data);
    } catch(e) { alert(e.response?.data?.detail || "Error — assign salary to employee first"); }
    finally { setComputing(false); }
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, employee_id:parseInt(form.employee_id) };
      await axios.post(APIConstant.taxDeclare, payload, ax());
      setOpen(false); fetchAll();
    } catch(e) { alert(e.response?.data?.detail || "Error saving declaration"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tax declaration?")) return;
    try { await axios.delete(`${APIConstant.taxDeclarations}${id}`, ax()); fetchAll(); }
    catch(e) { console.error(e); }
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>COMPENSATION</div>
          <h2>Tax & Deductions</h2>
          <p>Employee tax declarations, TDS computation and regime comparison</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnPrimary} onClick={() => openDeclare()}>
            <Scale size={14}/> New Declaration
          </button>
        </div>
      </div>

      <div className={`${styles.statsGrid} ${styles.cols4}`}>
        {[
          { label:"Declarations",    val:declarations.length,                                     color:"cyan"   },
          { label:"Old Regime",      val:declarations.filter(d=>d.regime==="Old").length,          color:"amber"  },
          { label:"New Regime",      val:declarations.filter(d=>d.regime==="New").length,          color:"green"  },
          { label:"Total TDS/Month", val:fmt(declarations.reduce((a,d)=>a+(d.monthly_tds||0),0)), color:"purple" },
        ].map(({ label, val, color }) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal} style={{ fontSize:22 }}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}><h3>Tax Declarations — {CURR_FY}</h3></div>
        {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr><th>Employee</th><th>Dept</th><th>Regime</th><th>80C</th><th>80D</th><th>HRA Paid</th><th>Taxable Income</th><th>Annual Tax</th><th>Monthly TDS</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {declarations.length === 0 ? (
                  <tr className={styles.emptyRow}><td colSpan={10}>No declarations yet — click "New Declaration"</td></tr>
                ) : declarations.map(d => (
                  <tr key={d.id}>
                    <td><div style={{ fontWeight:500 }}>{d.employee_name}</div><div style={{ fontSize:11, color:T.muted }}>{d.employee_code}</div></td>
                    <td className={styles.dim}>{d.department || "—"}</td>
                    <td><span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, fontWeight:600, background:`${REGIME_COLOR[d.regime]}20`, color:REGIME_COLOR[d.regime] }}>{d.regime}</span></td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace", fontSize:12 }}>{fmt(d.section_80c)}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace", fontSize:12 }}>{fmt(d.section_80d)}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace", fontSize:12 }}>{fmt(d.house_rent_paid)}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace", color:T.amber }}>{fmt(d.total_taxable_income)}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace", color:T.red, fontWeight:600 }}>{fmt(d.total_tax)}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace", color:T.green, fontWeight:700 }}>{fmt(d.monthly_tds)}</td>
                    <td>
                      <div style={{ display:"flex", gap:6 }}>
                        <button className={styles.btnSm} onClick={() => openDeclare(d)}>Edit</button>
                        <button className={styles.btnSm} style={{ color:T.red, borderColor:`${T.red}40` }} onClick={() => handleDelete(d.id)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tax Declaration Modal */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
        <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:680, fontFamily:"Outfit,sans-serif", overflow:"hidden", maxHeight:"90vh", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Tax Declaration — {form.financial_year}</h3>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={() => setOpen(false)}><X size={16}/></button>
          </div>
          <div style={{ padding:24, overflowY:"auto", display:"flex", flexDirection:"column", gap:16 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
              <div>
                <label style={lbl}>Employee *</label>
                <select value={form.employee_id} onChange={e => { setForm({...form,employee_id:e.target.value}); setCompute(null); }} style={inp}>
                  <option value="">Select employee</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Financial Year</label>
                <input value={form.financial_year} onChange={e => setForm({...form,financial_year:e.target.value})} style={inp}/>
              </div>
              <div>
                <label style={lbl}>Tax Regime</label>
                <select value={form.regime} onChange={e => { setForm({...form,regime:e.target.value}); setCompute(null); }} style={inp}>
                  <option value="New">New Regime (Default)</option>
                  <option value="Old">Old Regime (With Deductions)</option>
                </select>
              </div>
            </div>

            {form.regime === "Old" && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:T.amber, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>Old Regime Deductions</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
                  {[
                    { key:"section_80c",      label:"Section 80C (max ₹1.5L)",  placeholder:"LIC, PPF, ELSS..."       },
                    { key:"section_80d",      label:"Section 80D (max ₹25K)",   placeholder:"Health Insurance"        },
                    { key:"house_rent_paid",  label:"House Rent Paid / month",  placeholder:"Monthly rent"            },
                    { key:"section_80e",      label:"Section 80E",              placeholder:"Education loan interest" },
                    { key:"section_80g",      label:"Section 80G",              placeholder:"Donations"               },
                    { key:"other_deductions", label:"Other Deductions",         placeholder:"Any other"               },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label style={lbl}>{label}</label>
                      <input type="number" min={0} value={form[key]} placeholder={placeholder}
                        onChange={e => setForm({...form,[key]:parseFloat(e.target.value)||0})} style={inp}/>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button className={styles.btnSecondary} onClick={handleCompute} disabled={computing || !form.employee_id}>
              <Calculator size={14}/> {computing ? "Computing…" : "Compute Tax & Compare Regimes"}
            </button>

            {compute && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                {["old_regime","new_regime"].map(regime => {
                  const data = compute[regime];
                  const isRecommended = compute.recommended_regime === (regime==="old_regime"?"Old":"New");
                  return (
                    <div key={regime} style={{ background:isRecommended?`${T.green}0F`:T.toggle_bg, border:`1px solid ${isRecommended?`${T.green}33`:T.toggle_bg}`, borderRadius:10, padding:16 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
                        <span style={{ fontSize:13, fontWeight:700, color:regime==="old_regime"?T.amber:T.cyan }}>{regime==="old_regime"?"Old":"New"} Regime</span>
                        {isRecommended && <span style={{ fontSize:10.5, padding:"2px 8px", borderRadius:10, background:`${T.green}26`, color:T.green, fontWeight:700 }}>✓ RECOMMENDED</span>}
                      </div>
                      {[
                        { label:"Taxable Income",   val:fmt(data.total_taxable_income) },
                        { label:"Annual Tax + Cess", val:fmt(data.total_tax)           },
                        { label:"Monthly TDS",       val:fmt(data.monthly_tds), hi:true },
                      ].map(({ label, val, hi }) => (
                        <div key={label} style={{ display:"flex", justifyContent:"space-between", marginBottom:8, fontSize:12.5 }}>
                          <span style={{ color:T.muted }}>{label}</span>
                          <span style={{ fontFamily:"JetBrains Mono,monospace", fontWeight:hi?700:400, color:hi?(isRecommended?T.green:T.amber):T.text }}>{val}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
                <div style={{ gridColumn:"1/-1", background:`${T.cyan}0F`, border:`1px solid ${T.cyan}1F`, borderRadius:8, padding:"10px 16px", fontSize:13, color:T.cyan, textAlign:"center" }}>
                  💡 You save <strong style={{ color:T.green }}>{fmt(compute.savings)}</strong>/year with the <strong>{compute.recommended_regime} Regime</strong>
                </div>
              </div>
            )}
          </div>
          <div style={{ padding:"16px 24px", borderTop:`1px solid ${T.border}`, display:"flex", gap:10, justifyContent:"flex-end", flexShrink:0 }}>
            <button onClick={() => setOpen(false)} className={styles.btnSecondary}>Cancel</button>
            <button onClick={handleSave} className={styles.btnPrimary}><CheckCircle size={13}/> Save Declaration</button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
